import React, { useCallback, useMemo } from 'react';

import AnomalyLocation from '../anomaly-location';
import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import CustomGeneralPhotos from '../../custom-general/custom-general-photos';
import CustomUTMAnomaly from '../../custom-utm/customUTMAnomaly';
import Checklist from '../checklist';
import GeneralDrawing from '../general-drawing';
import PID from '../pid';
import PDOSummaryReport from '../pdo-summary';
import RiskRating from '../../../pages/reports/riskRating';

import { getSplitData, matchPhrases } from '../../../../utils/utilities';
import { AnomalyLocationData, ChecklistItem, FVIPDOChecklistItem, FVIStructureItem, PDFImageProps, PIDData } from '../../../../utils/models';

import config from '../../../../utils/config';
import FVIPDOChecklistReport from '../visual-inspection-pdo/fvi-pdo-checklist-report';

interface StructureAnalyticsProps {
	additionalComment: string;
	checklistItems: ChecklistItem[];
	items: FVIStructureItem[];
	pdoChecklist: FVIPDOChecklistItem[];
	includePDO?: boolean;
	includeEmptyPDONotice?: boolean
	pidData: PIDData;
	anomalyLocationData: AnomalyLocationData;
}

const StructureAnalytics: React.FC<StructureAnalyticsProps> = ({ additionalComment, anomalyLocationData, checklistItems, pdoChecklist, includePDO = false, includeEmptyPDONotice = false, items, pidData }) => {
	const checklistHeaders = useMemo(() => {
		return [
			{
				label: 'Structure',
				value: ''
			},
		];
	}, []);

	const getAnomalyHeaders = useCallback((item: FVIStructureItem) => {
		return [
			{
				label: 'Structure Number',
				value: item.structureMemberName,
			},
		];
	}, []);

	const pdoItems = useMemo(() => {
		return items.map((item) => ({ ...item, hasPDO: includePDO ? 'yes' : 'no', hasPID: pidData.hasPID, pIDNumber: pidData.pIDNumber }));
	}, [items, includePDO, pidData]);

	return (
		<>
			<PDOSummaryReport 
				includeEmptyReportNotice={includeEmptyPDONotice}
				itemKey='structureMemberName' 
				itemLabel='Structure Member Name' 
				items={pdoItems} 
			/>

			{getSplitData<PDFImageProps>(pidData.pIDImages, 1)
				.map((pid, index: number) => (
					<PID 
						hasPID={pidData.hasPID}
						key={index} 
						pidImages={pid} 
						pidNumber={pidData.pIDNumber} 
						sectionTitle="Inspection Findings" 
						sectionSubtitle="P&ID" 
					/>
				))}

			<Checklist
				enableChecklistItemComment
				headers={checklistHeaders}
				checklistItems={checklistItems}
				additionalComment={additionalComment}
			/>

			{pdoChecklist && (
				<Conditional isVisible={pdoChecklist.length > 0}>
					<FVIPDOChecklistReport
						items={pdoChecklist}
						label="Structure Member Name"
						value=""
						hasPDO={includePDO}
					/>
				</Conditional>
			)}

			{items.map((item) =>(	
				<React.Fragment key={item.structureMemberName}>
					<Conditional isVisible={matchPhrases(item.hasAnomalies, 'yes')}>
						<CustomUTMAnomaly
							includePDO={includePDO}
							lineNumberID={item.id}
							anomalyItems={item.anomalyItems}
							headers={getAnomalyHeaders(item)}
							identifier={item.structureMemberName}
						/>
					</Conditional>
				
					<Conditional isVisible={matchPhrases(item.hasAnomalies, 'no')}>
						<GeneralDrawing
							identifier={item.structureMemberName}
							generalDrawing={item.generalDrawing} />
					</Conditional>

					<Conditional isVisible={item.generalPhotos.length > 0}>
						<CustomGeneralPhotos identifier={item.structureMemberName} label="Inspection Findings" subLabel="Photographic Record" photos={item.generalPhotos} />
					</Conditional>
				</React.Fragment>
			))}

			<Conditional isVisible={matchPhrases(anomalyLocationData.hasAnomalyLocations, 'yes') && anomalyLocationData.anomalyLocations.length > 0}>
				<AnomalyLocation locationImages={anomalyLocationData.anomalyLocations} />
			</Conditional>

			<Conditional isVisible={config.enableRiskRating}>
				<CustomSection sectionTitle="Risk Rating">
					<Conditional isVisible={items.length > 0}>
						<RiskRating data={items} />
					</Conditional>
				</CustomSection>
			</Conditional>
		</>
	);
};

export default StructureAnalytics;
